<template>
  <v-card flat>
    <!-- ===================== TOOLBAR ===================== -->
    <!-- El rango es compartido por las dos sub-pestañas y arranca igual que el reporte de
         plan de producción (horarios de la empresa). -->
    <div class="toolbar">
      <VueCtkDateTimePicker v-model="filtros.inicio" :disabled="cargando" :max-date="filtros.fin || undefined"
        format="YYYY-MM-DD HH:mm" formatted="YYYY-MM-DD HH:mm" first-day-of-week="1" noHeader overlay
        label="Inicio" button-now-translation="Ahora" class="tb-fecha" />
      <VueCtkDateTimePicker v-model="filtros.fin" :disabled="cargando" :min-date="filtros.inicio || undefined"
        format="YYYY-MM-DD HH:mm" formatted="YYYY-MM-DD HH:mm" first-day-of-week="1" noHeader overlay
        label="Fin" button-now-translation="Ahora" class="tb-fecha" />

      <v-btn small color="primary" :loading="cargando" @click="buscar">
        <v-icon left small>mdi-magnify</v-icon> Buscar
      </v-btn>
      <v-btn small text :disabled="cargando" @click="restablecerRango"
        title="Volver al rango que sale de los horarios de la empresa">
        <v-icon left small>mdi-calendar-refresh-outline</v-icon> Semana en curso
      </v-btn>

      <v-spacer />

      <div v-if="auditoria" class="tb-auditoria" :title="prop(auditoria, 'carpeta')">
        <v-icon x-small class="mr-1">mdi-folder-clock-outline</v-icon>
        Última carga
        <strong v-if="fechaAuditoria" class="ml-1">{{ fechaAuditoria }}</strong>
      </div>
    </div>

    <!-- SyncBridge escribe los CSV de la carga hasta terminar de crear las ODTs; mientras tanto el
         back responde con la carga anterior completa y avisa aquí cuál es la que sigue corriendo. -->
    <v-alert v-if="cargaEnCurso" type="warning" dense text icon="mdi-progress-clock" class="mx-3 mt-2 mb-0">
      Hay una carga de ODTs en curso<template v-if="fechaCargaEnCurso"> desde las <strong>{{ fechaCargaEnCurso }}</strong></template>.
      Se muestran los datos de la carga anterior<template v-if="fechaAuditoria"> ({{ fechaAuditoria }})</template>.
    </v-alert>

    <v-tabs v-model="tab" background-color="transparent" show-arrows>
      <v-tab>
        <v-icon left small>mdi-format-list-bulleted</v-icon> Información
      </v-tab>
      <v-tab>
        <v-icon left small>mdi-scale-balance</v-icon> Comparativa
      </v-tab>
      <v-tab>
        <v-icon left small>mdi-file-delimited-outline</v-icon> CSV completo
      </v-tab>
      <v-tab>
        <v-icon left small>mdi-file-excel-outline</v-icon> Comparativo Excel
      </v-tab>

      <v-tabs-items v-model="tab" touchless>
        <v-tab-item eager>
          <Informacion ref="informacion" @cargando="onCargando" @auditoria="onAuditoria"
            @carga-en-curso="onCargaEnCurso" />
        </v-tab-item>
        <v-tab-item eager>
          <Comparativa ref="comparativa" @cargando="onCargando" @auditoria="onAuditoria"
            @carga-en-curso="onCargaEnCurso" />
        </v-tab-item>
        <v-tab-item eager>
          <ArchivoCsv ref="archivoCsv" @cargando="onCargando" @auditoria="onAuditoria"
            @carga-en-curso="onCargaEnCurso" />
        </v-tab-item>
        <v-tab-item eager>
          <ComparativoExcel ref="comparativoExcel" @cargando="onCargando" />
        </v-tab-item>
      </v-tabs-items>
    </v-tabs>
  </v-card>
</template>

<script>
import axios from 'axios'
import Informacion from './CargaOts/Informacion.vue'
import Comparativa from './CargaOts/Comparativa.vue'
import ArchivoCsv from './CargaOts/ArchivoCsv.vue'
import ComparativoExcel from './CargaOts/ComparativoExcel.vue'
import { prop, fmtFecha, fmtFiltro, filtroAIso } from './CargaOts/utils'

export default {
  name: 'CargaOts',

  components: { Informacion, Comparativa, ArchivoCsv, ComparativoExcel },

  data() {
    return {
      tab: 0,
      cargando: false,
      listo: false,
      auditoria: null,
      // Carga que sigue corriendo (su carpeta aún no tiene CSV): null si la última ya sirve.
      cargaEnCurso: null,
      // Cada sub-pestaña se consulta la primera vez que se abre y se marca como cargada;
      // "Buscar" o un cambio de rango las invalida para que vuelvan a pedir datos.
      cargadas: [false, false, false, false],
      filtros: {
        inicio: '',
        fin: '',
      },
      // Fin del rango inicial: 'semana' (una semana exacta) u 'horarioEmpresa' (última hora
      // del último día configurado). Se lee de la config del reporte de plan de producción
      // (r100) para que esta pestaña arranque con el mismo rango que ese reporte.
      modoFinPeriodo: 'semana',
    }
  },

  computed: {
    fechaAuditoria() {
      const f = prop(this.auditoria, 'fecha')
      return f ? fmtFecha(f) : ''
    },

    fechaCargaEnCurso() {
      const f = prop(this.cargaEnCurso, 'fecha')
      return f ? fmtFecha(f) : ''
    },
  },

  watch: {
    tab() {
      this.cargarActiva()
    },
  },

  async mounted() {
    // Mismo arranque que el reporte de plan de producción: primero la config que decide cómo
    // se calcula el fin del rango, y luego el rango a partir de los horarios activos de la
    // empresa (CAT_Horarios). Solo si eso falla se cae a la semana en curso.
    await this.cargarModoFinPeriodo()
    await this.inicializarDesdeHorarios()
    this.listo = true
    this.cargarActiva()
  },

  methods: {
    prop,

    // Punto de entrada desde la pestaña de Producción (Lista.vue).
    initialize() {
      if (!this.listo) return
      this.buscar()
    },

    onCargando(valor) {
      this.cargando = !!valor
    },

    onAuditoria(valor) {
      if (valor) this.auditoria = valor
    },

    // A diferencia de la auditoría, aquí null sí importa: significa que la carga ya terminó.
    onCargaEnCurso(valor) {
      this.cargaEnCurso = valor || null
    },

    buscar() {
      this.cargadas = [false, false, false, false]
      this.cargarActiva()
    },

    // Consulta la sub-pestaña visible si todavía no tiene datos del rango vigente. La del CSV
    // completo ignora el rango, pero se recarga igual con "Buscar" por si hubo una carga nueva.
    cargarActiva() {
      if (!this.listo || this.cargadas[this.tab]) return
      const hijo = [this.$refs.informacion, this.$refs.comparativa, this.$refs.archivoCsv,
        this.$refs.comparativoExcel][this.tab]
      if (!hijo) return
      this.$set(this.cargadas, this.tab, true)
      hijo.cargar(filtroAIso(this.filtros.inicio), filtroAIso(this.filtros.fin))
    },

    async restablecerRango() {
      await this.inicializarDesdeHorarios()
      this.buscar()
    },

    // ===================== Rango por defecto (horarios de la empresa) =====================

    // Lunes (00:00) de la semana en curso.
    lunesSemanaActual() {
      const hoy = new Date()
      const diff = (hoy.getDay() + 6) % 7 // lunes = 0
      const lunes = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
      lunes.setDate(lunes.getDate() - diff)
      return lunes
    },

    // Fallback síncrono: semana en curso (lunes 00:00 → lunes siguiente 00:00).
    inicializarSemana() {
      const inicio = this.lunesSemanaActual()
      const fin = new Date(inicio)
      fin.setDate(inicio.getDate() + 7)
      this.filtros.inicio = fmtFiltro(inicio)
      this.filtros.fin = fmtFiltro(fin)
    },

    // Calcula el rango por defecto a partir de los horarios activos (CAT_Horarios): inicia en
    // la primera hora del día con menor diaSemana (0=domingo … 6=sábado). Según la configuración
    // del reporte, termina una semana después o en la última hora del día con mayor diaSemana.
    async inicializarDesdeHorarios() {
      try {
        // /Horarios responde { events, start, intervals }: los horarios están en events.
        const { data } = await axios.get('/Horarios')
        const horarios = Array.isArray(data)
          ? data
          : Array.isArray(data && data.events)
            ? data.events
            : []
        if (!horarios.length) {
          this.inicializarSemana()
          return
        }

        const minutosHora = (valor) => {
          const [hora, minuto] = String(valor || '00:00').split(':').map(Number)
          return (hora || 0) * 60 + (minuto || 0)
        }
        const horariosValidos = horarios.filter((h) => {
          const dia = Number(prop(h, 'diaSemana'))
          return Number.isInteger(dia) && dia >= 0 && dia <= 6
        })
        if (!horariosValidos.length) {
          this.inicializarSemana()
          return
        }

        const primerDia = Math.min(...horariosValidos.map((h) => Number(prop(h, 'diaSemana'))))
        const ultimoDia = Math.max(...horariosValidos.map((h) => Number(prop(h, 'diaSemana'))))
        const primerHorario = horariosValidos
          .filter((h) => Number(prop(h, 'diaSemana')) === primerDia)
          .reduce((primero, h) =>
            minutosHora(prop(h, 'inicio')) < minutosHora(prop(primero, 'inicio')) ? h : primero,
          )
        const ultimoHorario = horariosValidos
          .filter((h) => Number(prop(h, 'diaSemana')) === ultimoDia)
          .reduce((ultimo, h) =>
            minutosHora(prop(h, 'fin')) > minutosHora(prop(ultimo, 'fin')) ? h : ultimo,
          )
        const [hhInicio, mmInicio] = String(prop(primerHorario, 'inicio') || '00:00').split(':').map(Number)
        const [hhFin, mmFin] = String(prop(ultimoHorario, 'fin') || '00:00').split(':').map(Number)

        // Última ocurrencia de ese día de la semana en o antes de hoy
        // (0=domingo … 6=sábado), p. ej. domingo → el último domingo desde hoy.
        const hoy = new Date()
        const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
        const diff = (inicio.getDay() - primerDia + 7) % 7
        inicio.setDate(inicio.getDate() - diff)
        inicio.setHours(hhInicio || 0, mmInicio || 0, 0, 0)

        const fin = new Date(inicio)
        if (this.modoFinPeriodo === 'horarioEmpresa') {
          fin.setDate(inicio.getDate() + (ultimoDia - primerDia))
          fin.setHours(hhFin || 0, mmFin || 0, 0, 0)
          // Un único horario puede cruzar medianoche (p. ej. 22:00 → 06:00).
          if (fin <= inicio) fin.setDate(fin.getDate() + 1)
        } else {
          fin.setDate(inicio.getDate() + 7)
        }

        this.filtros.inicio = fmtFiltro(inicio)
        this.filtros.fin = fmtFiltro(fin)
      } catch (e) {
        // Si falla, se usa el fallback de la semana en curso.
        this.inicializarSemana()
        // eslint-disable-next-line no-console
        console.error('Error cargando horarios para fechas por defecto', e)
      }
    },

    // Lee (sin escribir) la configuración del reporte de plan de producción para saber cómo
    // termina el rango. Si no existe o falla, se queda con el default 'semana'.
    async cargarModoFinPeriodo() {
      try {
        const { data } = await axios.get('ReportesConfig/Obtener/r100')
        let cfg = data
        const raw = prop(cfg, 'configJson')
        if (raw != null) cfg = typeof raw === 'string' ? JSON.parse(raw) : raw
        this.modoFinPeriodo = (cfg && cfg.modoFinPeriodo === 'horarioEmpresa')
          ? 'horarioEmpresa'
          : 'semana'
      } catch (e) {
        this.modoFinPeriodo = 'semana'
      }
    },
  },
}
</script>

<style scoped>
.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.tb-fecha {
  max-width: 210px;
  min-width: 170px;
}

.tb-auditoria {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
}

/* El picker trae su propio input: se iguala al alto denso de los controles de Vuetify. */
.toolbar >>> .date-time-picker input {
  height: 40px;
}
</style>
